import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { CONTACT } from '../lib/contactInfo';
import Logo from './Logo';
import BackgroundPattern from './BackgroundPattern';

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M16.6 3h-3.2v13.1c0 1.55-1.25 2.82-2.8 2.82s-2.8-1.27-2.8-2.82c0-1.52 1.22-2.77 2.72-2.82v-3.2c-3.28.05-5.92 2.73-5.92 6.02 0 3.32 2.7 6.02 6 6.02s6-2.7 6-6.02V9.4c1.16.85 2.6 1.35 4.15 1.35V7.55c-2.3 0-4.15-1.87-4.15-4.18V3Z" />
  </svg>
);

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 32 32" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M16.001 3C9.11 3 3.5 8.61 3.5 15.5c0 2.34.63 4.6 1.83 6.6L3 29l7.1-2.28a12.44 12.44 0 0 0 5.9 1.5h.01c6.9 0 12.5-5.6 12.5-12.5S22.9 3 16 3Zm0 22.79h-.01a10.3 10.3 0 0 1-5.25-1.44l-.38-.22-4.21 1.35 1.37-4.1-.24-.42a10.24 10.24 0 0 1-1.58-5.46c0-5.68 4.62-10.3 10.31-10.3 2.75 0 5.34 1.08 7.28 3.02a10.23 10.23 0 0 1 3.02 7.29c0 5.68-4.63 10.28-10.31 10.28Zm5.65-7.71c-.31-.15-1.83-.9-2.11-1-.28-.1-.49-.15-.69.16-.2.3-.79 1-.97 1.2-.18.2-.36.23-.67.08-.31-.16-1.29-.48-2.46-1.53-.91-.81-1.52-1.82-1.7-2.13-.18-.31-.02-.47.13-.62.14-.14.31-.36.46-.54.15-.18.2-.3.31-.5.1-.2.05-.38-.02-.53-.08-.16-.69-1.67-.95-2.28-.25-.6-.5-.52-.69-.53-.18-.01-.38-.01-.58-.01-.2 0-.53.08-.81.38-.28.3-1.06 1.04-1.06 2.53s1.09 2.94 1.24 3.14c.15.2 2.15 3.28 5.2 4.6.73.31 1.29.5 1.73.64.73.23 1.39.2 1.91.12.58-.09 1.83-.75 2.09-1.47.26-.72.26-1.34.18-1.47-.07-.14-.27-.22-.58-.37Z" />
  </svg>
);

const Footer = () => {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();

  const socials = [
    { name: 'Instagram', href: CONTACT.instagram, Icon: Instagram },
    { name: 'Facebook', href: CONTACT.facebook, Icon: Facebook },
    { name: 'TikTok', href: CONTACT.tiktok, Icon: TikTokIcon },
  ];

  const contactIcons = [
    { name: lang === 'ar' ? 'البريد' : 'Email', href: `mailto:${CONTACT.email}`, Icon: Mail },
    { name: lang === 'ar' ? 'الهاتف' : 'Phone', href: `tel:${CONTACT.phoneTel}`, Icon: Phone },
    { name: lang === 'ar' ? 'الموقع' : 'Location', href: CONTACT.mapShareUrl, Icon: MapPin },
    { name: 'WhatsApp', href: `https://wa.me/${CONTACT.whatsappNumber}`, Icon: WhatsAppIcon },
  ];

  const quickLinks = [
    { to: '/about', label: t('aboutUs') },
    { to: '/contact', label: t('contact') },
    { to: '/shop', label: t('shop') },
  ];

  return (
    <footer className="relative overflow-hidden bg-gold-400 text-[#171412]">
      {/* Admin-uploaded footer image (Admin > Settings), gold-washed so text/icons stay legible.
          Falls back to the faint bead-pattern watermark when no image is set. */}
      {settings.footerImage ? (
        <div className="absolute inset-0">
          <img src={settings.footerImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gold-400/70" />
        </div>
      ) : (
        <BackgroundPattern className="text-[#171412]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/10 pointer-events-none" />

      <div className="relative container mx-auto px-4 py-8 md:py-10 text-center">
        <div className="flex flex-col items-center gap-1 mb-6">
          <Logo className="h-20 w-20" />
          <div className="font-bold text-lg text-[#171412]">{lang === 'ar' ? 'برستيج مسابيح' : 'Prestige Mesbah'}</div>
          <p className="text-xs text-[#171412]/70 max-w-xs">{t('footerTagline')}</p>
        </div>

        <h4 className="font-bold text-xl md:text-2xl mb-4 text-[#171412]" style={{ fontFamily: "'Amiri', serif" }}>
          {lang === 'ar' ? 'تابعنا' : 'Follow Us'}
        </h4>
        <div className="flex justify-center flex-wrap gap-4 mb-6">
          {socials.map(({ name, href, Icon }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={name}
              title={name}
              className="w-14 h-14 rounded-full bg-[#171412] text-white flex items-center justify-center shadow-md hover:-translate-y-1 hover:shadow-xl transition-all"
            >
              <Icon className="w-6 h-6" />
            </a>
          ))}
        </div>

        <h4 className="font-bold text-xl md:text-2xl mb-4 text-[#171412]" style={{ fontFamily: "'Amiri', serif" }}>
          {t('contact')}
        </h4>
        <div className="flex justify-center flex-wrap gap-4 mb-6">
          {contactIcons.map(({ name, href, Icon }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={name}
              title={name}
              className="w-14 h-14 rounded-full bg-[#171412] text-white flex items-center justify-center shadow-md hover:-translate-y-1 hover:shadow-xl transition-all"
            >
              <Icon className="w-6 h-6" />
            </a>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          {quickLinks.map((l) => (
            <Link key={l.to} to={l.to} className="text-sm font-bold text-[#171412]/80 hover:text-[#171412] underline underline-offset-4 decoration-[#171412]/30">
              {l.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="relative border-t border-[#171412]/15 py-4">
        <div className="container mx-auto px-4 text-center text-xs font-semibold text-[#171412]/70">
          &copy; {new Date().getFullYear()} {lang === 'ar' ? 'برستيج مسابيح' : 'Prestige Mesbah'} - {t('allRightsReserved')}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
